import React, { useState } from 'react';
import {
	Box,
	Paper,
	Skeleton,
	Stack,
	Table,
	TableBody,
	TableContainer,
	Typography,
	useTheme,
} from '@mui/material';
import { FolioTableHead } from '@/components/table/FolioTableHead';
import { useObjectContext } from '@/hooks/useObjectContext';
import { PolicyRow } from '@/components/tables/PolicyRow';
import { NavigateInsightButton } from '@/components/buttons/NavigateInsightButton';
import { useParams } from 'react-router-dom';
import { useGetDealPoliciesQuery } from '@/features/deals/dealsApi';
import { getDealClientId } from '@/features/deals/helper';
import { useDispatch } from 'react-redux';
import { openDialog } from '@/features/dialog/dialogSlice';
import { GetMenuLink } from '@/components/tables/PolicyTransactionTable/GetMenuLink';
import { minimalLabelTheme } from '@/app/theme';
import { usePipelineContext } from '@/context/pipelineContext';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { EditPolicyTransactionButton } from '@/components/buttons/viewHeaderButtons/EditPolicyTransactionsButton';
import { safelyParseDate } from '@/utils/date';

const policyHeadCells = [
	{
		id: 'invoiceNumber',
		numeric: false,
		label: 'Invoice No. Description',
		align: 'left',
		sortable: false,
		width: '40%',
		minWidth: 400,
	},
	{
		id: 'policyNumber',
		numeric: false,
		label: 'Policy No. Class of Risk',
		align: 'left',
		sortable: false,
		width: '12%',
		minWidth: 120,
	},
	{
		id: 'transactionType',
		numeric: false,
		label: 'Transaction Type',
		align: 'left',
		sortable: false,
		width: '12%',
		minWidth: 120,
	},
	{
		id: 'policyPeriod',
		numeric: false,
		label: 'Policy Period',
		align: 'left',
		sortable: false,
		width: '10%',
		minWidth: 100,
	},
	{
		id: 'insurer',
		numeric: false,
		label: 'Insurer',
		align: 'left',
		sortable: false,
		width: '15%',
		minWidth: 150,
	},
	{
		id: 'latestTransaction',
		numeric: false,
		label: 'Latest Transaction',
		align: 'left',
		sortable: false,
		width: '10%',
		minWidth: 100,
	},
	{
		id: 'empty',
		numeric: false,
		label: '',
		align: 'left',
		sortable: false,
		width: '5%',
		minWidth: 50,
	},
];

export const PoliciesTab = () => {
	const theme = useTheme();
	const { clientRow, dealRow, dealId } = useObjectContext();

	const dispatch = useDispatch();

	const { data: policyData, ...policiesQuery } = useGetDealPoliciesQuery(
		{ dealId: dealId },
		{ skip: !dealId },
	);

	const policies = policyData?.policies ?? [];
	const transactions = policyData?.transactions ?? [];
	const order = 'desc';
	const orderBy = 'CreatedWhen';

	const { getFeatureFlags } = useFeatureFlagContext();
	const showAddPolicy = Boolean(getFeatureFlags('addPolicy')?.active);

	const { pipeline } = usePipelineContext(dealRow?.pipeline);

	const pipelineName =
		dealRow?.pipeline === process.env.REACT_APP_PIPELINE_NEW_BUSINESS
			? 'New Business'
			: `${(pipeline?.label ?? '').slice(0, -1)}`;

	const hasPolicies = policies?.length > 0;

	const groupTransactionsByPolicy = () => {
		const grouped = {};

		transactions.forEach((transaction) => {
			const policyId = transaction.PolicyId;
			if (!grouped[policyId]) {
				grouped[policyId] = [];
			}
			grouped[policyId].push(transaction);
		});

		return grouped;
	};

	const getUniquePolicies = () => {
		const unique = {};
		policies.forEach((policy) => {
			if (!unique[policy.Id]) {
				unique[policy.Id] = policy;
			}
		});
		return Object.values(unique);
	};

	const transactionsByPolicy = groupTransactionsByPolicy();
	const uniquePolicies = getUniquePolicies();

	const boxSx = {
		p: 3,
		textAlign: 'center',
		border: `1px solid ${theme.palette.divider}`,
		borderRadius: '6px',
		backgroundColor: theme.palette.mode === 'dark' ? 'neutral.800' : 'neutral.100',
	};

	const paperProps = {
		elevation: 1,
		sx: {
			width: '100%',
			overflow: 'hidden',
			border: `1px solid ${theme.palette.divider}`,
			borderRadius: '6px',
			padding: 3,
		},
	};

	const NoPoliciesDisplay = ({ header, text, policy }) => (
		<Box sx={boxSx}>
			<Stack spacing={1} direction={'column'} alignItems='center'>
				{header && (
					<Typography variant='subtitle1' align='center' sx={{ fontWeight: 'bold' }}>
						{header}
					</Typography>
				)}
				<Typography sx={minimalLabelTheme} align='center' mb={2}>
					{text}
				</Typography>
				<Stack direction='row' spacing={3} mt={2} justifyContent='center'>
					<NavigateInsightButton insightId={clientRow?.insightId} policy={policy} />

					{policy && <EditPolicyTransactionButton minimal={true} policy={policy} />}
				</Stack>
			</Stack>
		</Box>
	);

	if (policiesQuery.isLoading) {
		return (
			<Skeleton
				variant='rectangular'
				width='100%'
				height={150}
				sx={{
					borderRadius: '8px',
					backgroundColor: theme.palette.mode === 'dark' ? 'neutral.800' : 'neutral.100',
				}}
			/>
		);
	}

	if (!hasPolicies) {
		return (
			<Paper {...paperProps}>
				<NoPoliciesDisplay
					text={`No policies linked. Complete your ${pipelineName} to see them here.`}
				/>
			</Paper>
		);
	}

	return (
		<Stack spacing={3}>
			{uniquePolicies.map((policy) => {
				const policyTransactions = transactionsByPolicy[policy.Id] || [];
				const hasPolicyTransactions = policyTransactions.length > 0;

				return (
					<Paper key={`policy-${policy.Id}`} {...paperProps}>
						<Stack
							direction='row'
							justifyContent='space-between'
							alignItems='center'
							spacing={1}
						>
							<Typography sx={{ fontWeight: 'bold', mr: 1 }}>
								{`${policy.ClassOfRisk} - ${policy.PolicyNumber}`}
							</Typography>

							{showAddPolicy && (
								<GetMenuLink deal={dealRow} hideViewDeal={true} policy={policy} />
							)}
						</Stack>
						{hasPolicyTransactions ? (
							<TableContainer
								component={Paper}
								sx={{
									borderRadius: '4px',
									mt: 1,
								}}
							>
								<Table
									aria-labelledby='tableTitle'
									size='small'
									sx={{
										minWidth: 600,
										tableLayout: 'fixed',
										'& .MuiTableCell-root': {
											padding: '8px 12px',
											fontSize: '0.875rem',
											width: (headCell) => headCell.width || 'auto',
											minWidth: (headCell) => headCell.minWidth || 'auto',
										},
										'& .MuiTableRow-root': {
											height: '48px',
										},
									}}
								>
									<FolioTableHead
										headerBackground={true}
										order={order}
										orderBy={orderBy}
										headCells={policyHeadCells}
										sx={{
											backgroundColor:
												theme.palette.mode === 'dark'
													? 'neutral.800'
													: 'neutral.100',
											'& .MuiTableCell-root': {
												fontWeight: 'bold',
												borderBottom: `2px solid ${theme.palette.divider}`,
												padding: '10px 12px',
												fontSize: '0.875rem',
												width: (headCell) => headCell.width || 'auto',
												minWidth: (headCell) => headCell.minWidth || 'auto',
												overflow: 'visible',
												whiteSpace: 'normal',
											},
										}}
									/>
									<TableBody>
										{[...policyTransactions]
											.sort((a, b) => {
												if (a?.IsLatestInPeriod && !b?.IsLatestInPeriod)
													return -1;
												if (!a?.IsLatestInPeriod && b?.IsLatestInPeriod)
													return 1;
												return (
													safelyParseDate(b?.PostedWhen) -
													safelyParseDate(a?.PostedWhen)
												);
											})

											.map((item, index) => (
												<PolicyRow
													hideCheckbox={true}
													key={`transaction-row-${policy.Id}-${index}-${item.Id}`}
													policy={item}
													showStatus={true}
													showDescription={false}
													canRemoveTransaction={true}
													dealId={dealRow?.dealId}
													clientId={getDealClientId(dealRow)}
												/>
											))}
									</TableBody>
								</Table>
							</TableContainer>
						) : (
							<NoPoliciesDisplay
								header={'Policy transactions can be added here.'}
								text={`Associate policy transactions to this deal below or when completing ${dealRow?.pipelineFolder?.toLowerCase()}`}
								policy={policy}
							/>
						)}
					</Paper>
				);
			})}
		</Stack>
	);
};
